import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import InfiniteFeedRow from '@cont/SWRInfiniteFeedRow/InfiniteFeedRow';
import FeedTypeBlock from '@cont/SWRInfiniteFeedRow/DynamicFeedTypeBlock';
import {
  isTestSeriesEntityType,
  isEducatorEntityType,
  isCourseEntityType,
  isCarouselEntityType,
  isBannerEntityType
} from '@cont/SWRInfiniteFeedRow/utils/getBlockType';
import useTopology from '@api/hooks/topology/useTopology';
import { toggleEnrolledCourse } from '@actions/courses';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import PropTypes from 'prop-types';
import useEntireFeed from './useEntireFeed';

const LoaderWrapper = styled.div`
  grid-column: span 18;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const renderLoader = () => (
  <LoaderWrapper>
    <CircularLoader />
  </LoaderWrapper>
);

const EntireFeed = ({ sectionMetaInfo }) => {
  const router = useRouter();
  const { goalUID } = router.query;
  const dispatch = useDispatch();

  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const { topology: goalTopology } = useTopology(goalUID, {
    revalidateOnMount: false
  });

  const {
    data,
    isValidating,
    mutate: refetchEntireFeed,
    fetchMoreData
  } = useEntireFeed({
    goalUID
  });

  useEffect(() => {
    if (isLoggedIn) {
      refetchEntireFeed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const enroll = (course) => () => {
    return dispatch(toggleEnrolledCourse(course.uid, !course.isEnrolled));
  };

  const { goalSlug } = goalTopology;

  const getLinkAndHrefProps = (type, max, min) => {
    const viewAllLink =
      max > min ? `/goal/${goalSlug}/${goalUID}/classes/${type}` : null;
    const viewAllHref = '/goal/[goalSlug]/[goalUID]/classes/[type]';

    return {
      viewAllLink,
      viewAllHref
    };
  };

  const renderItem = (feed, index) => {
    const feedType = feed.type;
    const feedEntityType = feed.extraBlockinfo.entityType;

    let updatedFeed = { ...feed };
    if (sectionMetaInfo[feedType]) {
      const { label, description } = sectionMetaInfo[feedType];
      updatedFeed = { ...updatedFeed, label, description };
    }
    let extraProps = {
      goalSlug,
      goalUID,
      index,
      goal: goalTopology,
      lastPrimarySource: 'Special Class Feed',
      type: 'special',
      refetchEntireFeed
    };

    if (isTestSeriesEntityType(feedEntityType)) {
      const feedCount = feed.extraBlockinfo?.count || 0;
      extraProps = {
        ...extraProps,
        ...getLinkAndHrefProps(feedType, feedCount, 3)
      };
    }

    if (isEducatorEntityType(feedEntityType)) {
      const feedCount = feed.extraBlockinfo?.count || 0;
      extraProps = {
        ...extraProps,
        ...getLinkAndHrefProps(feedType, feedCount, 0)
      };
    }

    if (isCourseEntityType(feedEntityType)) {
      const feedCount = feed.extraBlockinfo?.count || 0;

      extraProps = {
        ...extraProps,
        ...getLinkAndHrefProps(feedType, feedCount, 3)
      };
    }

    if (
      isCarouselEntityType(feedEntityType) ||
      isBannerEntityType(feedEntityType)
    ) {
      extraProps.onEnroll = enroll;
    }

    return (
      <FeedTypeBlock
        key={feedType}
        feedBlock={updatedFeed}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...extraProps}
      />
    );
  };

  return (
    <InfiniteFeedRow
      data={data.results}
      isLoading={isValidating}
      renderItem={renderItem}
      renderLoader={renderLoader}
      onLoadMore={fetchMoreData}
    />
  );
};

export default EntireFeed;

EntireFeed.propTypes = {
  sectionMetaInfo: PropTypes.objectOf(PropTypes.any)
};

EntireFeed.defaultProps = {
  sectionMetaInfo: {}
};
